import React from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { SmartToyOutlined } from "@mui/icons-material";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";
import { GlobalContext } from "../../context/GlobalContext";

const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : "No messages yet";

const LeadCloserConversations = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [query, setQuery] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [detail, setDetail] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      limit: "25",
      ...(query.trim() ? { q: query.trim() } : {}),
      ...(status ? { status } : {}),
    });
    const res = await hitAxios({
      path: `/api/admin/lead-closer/conversations?${params}`,
      post: false,
      admin: true,
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setRows(res.data.data || []);
      setTotal(Number(res.data.pagination?.total) || 0);
    }
    setLoading(false);
  }, [hitAxios, page, query, status]);

  React.useEffect(() => {
    const timer = window.setTimeout(load, 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  const openConversation = async (id) => {
    setDetailLoading(true);
    setDetail({ conversation: { id }, messages: [] });
    const res = await hitAxios({
      path: `/api/admin/lead-closer/conversations/${id}`,
      post: false,
      admin: true,
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success) setDetail(res.data.data);
    setDetailLoading(false);
  };

  const contacted = rows.filter((row) => row.email).length;
  const active = rows.filter((row) => Number(row.messageCount) > 0).length;

  return (
    <Box>
      <PageHeader
        title="Beyund Assistant"
        subtitle="Review plan questions, buying intent and contact details from pricing visitors."
        icon={SmartToyOutlined}
        gradientIcon
      />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
        {[
          ["Conversations", total],
          ["Active on this page", active],
          ["Contacts on this page", contacted],
        ].map(([label, value]) => (
          <Card key={label} variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h4" fontWeight={900}>
              {value}
            </Typography>
          </Card>
        ))}
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <TextField
            size="small"
            label="Search name or email"
            value={query}
            onChange={(event) => {
              setPage(1);
              setQuery(event.target.value);
            }}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setPage(1);
                setStatus(event.target.value);
              }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {["open", "qualified", "converted", "closed", "blocked"].map((item) => (
                <MenuItem key={item} value={item}>
                  {item[0].toUpperCase() + item.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Visitor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Questions</TableCell>
                <TableCell>Last activity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length ? (
                rows.map((row) => (
                  <TableRow
                    hover
                    key={row.id}
                    onClick={() => openConversation(row.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {row.name || row.email || `Anonymous visitor #${row.id}`}
                      </Typography>
                      {row.email && row.name && (
                        <Typography variant="caption" color="text.secondary">
                          {row.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status || "open"} />
                    </TableCell>
                    <TableCell align="right">{row.messageCount || 0}</TableCell>
                    <TableCell>{formatDate(row.lastMessageAt || row.createdAt)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No conversations found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 25 && (
          <Stack alignItems="center" sx={{ p: 2 }}>
            <Pagination
              page={page}
              count={Math.ceil(total / 25)}
              onChange={(_, next) => setPage(next)}
            />
          </Stack>
        )}
      </Card>

      <CommonDialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        title={
          detail?.conversation?.name ||
          detail?.conversation?.email ||
          `Conversation #${detail?.conversation?.id || ""}`
        }
        subtitle={detail?.conversation?.email || "Anonymous pricing visitor"}
        icon={SmartToyOutlined}
        maxWidth="md"
      >
        {detailLoading ? (
          <Stack alignItems="center" py={6}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {detail?.messages?.length ? (
              detail.messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "88%",
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: message.role === "user" ? "primary.main" : "action.hover",
                    color: message.role === "user" ? "primary.contrastText" : "text.primary",
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.75, display: "block", mb: 0.5 }}>
                    {message.role === "user" ? "Visitor" : "Beyund Assistant"} ·{" "}
                    {formatDate(message.createdAt)}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                    {message.content}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography color="text.secondary">No messages in this conversation.</Typography>
            )}
          </Stack>
        )}
      </CommonDialog>
    </Box>
  );
};

export default LeadCloserConversations;
